import { lazy } from 'react';
import { type ShouldRevalidateFunctionArgs } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { CreateListingApplicationFromTmpStorageContainer } from '#/modules/listing/containers/CreateListingApplicationFromTmpStorageContainer';
import { getListingPublishedDetails } from '#/modules/listing/helpers/getListingPublishedDetails';
import { isListingExpired } from '#/modules/listing/helpers/isListingExpired';
import { listingApiIsomorphicFetchJob } from '#/modules/listing/jobs/api/listingApiFetchJob';
import { viewJobReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { ClientOnlyLazySearchJobsRefinements } from '#/modules/search/jobs/components/Refinements/ClientOnlyLazySearchJobsRefinements';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewJobPage } from './ViewJobPage';

export const handle = {
  defaultSearchType: 'JOB',
  showDesktopSearch: true,
  showSearchRefinements: true,
  searchRefinements: <ClientOnlyLazySearchJobsRefinements />,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedListingApplicationTmpId =
    currentUrl.searchParams.has('listingApplicationTmpId') &&
    !nextUrl.searchParams.has('listingApplicationTmpId');

  if (removedListingApplicationTmpId) return false;

  return defaultShouldRevalidate;
}

const LazyExpiredJobPage = lazy(() =>
  import('#/modules/listing/jobs/pages/expired/ExpiredJobPage').then((m) => ({
    default: m.ExpiredJobPage,
  })),
);

const LazyUnpublishedJobPage = lazy(() =>
  import('#/modules/listing/jobs/pages/unpublished/UnpublishedJobPage').then(
    (m) => ({
      default: m.UnpublishedJobPage,
    }),
  ),
);

export async function loader({
  request,
  context,
  params: { jobIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewJobReferenceRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const [jobId] = jobIdDashSlug.split('-');

  try {
    const [job, listingExtras] = await Promise.all([
      listingApiIsomorphicFetchJob({ request, context })(jobId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiIsomorphicFetchListingExtras({
            request,
            context,
          })([jobId])
        : null,
    ]);

    const { saved, applicationId } = listingExtras?.[jobId] ?? {
      saved: false,
      applicationId: null,
    };

    switch (job.type) {
      case 'JOB': {
        const expired = isListingExpired(job.expires);

        return {
          type: expired ? 'EXPIRED_JOB' : job.type,
          job,
          saved,
          applicationId,
          publishedDetails: getListingPublishedDetails({
            publishedDate: job.firstPublished,
            status: expired
              ? ListingViewStatus.EXPIRED
              : ListingViewStatus.PUBLISHED,
            context,
          }),
          locale: serverContext.locale,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: job.url,
          } satisfies LayoutHandleProps,
        } as const;
      }
      case 'UNPUBLISHED_JOB':
        return idealistNotFound({
          type: 'UNPUBLISHED_JOB',
          unpublishedJob: job,
        } as const);
    }
  } catch {
    return idealistNotFound(null);
  }
}

export default function ViewJobHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  // NOTE: UNPUBLISHED_JOB is not the same as Job.isPublished === false
  // UNPUBLISHED_JOB is returned when we are serving an unpublished (hidden) job
  // to a user who has not applied to it.
  // Previous Applicants would fall under the JOB branch (Job.type === 'JOB')
  // Context: https://idealist.slack.com/archives/CHJ9JK7TP/p1749664131909249?thread_ts=1747839567.113479&cid=CHJ9JK7TP
  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'JOB': {
      const orgName = loaderData.job.isPostedAnonymously
        ? loaderData.job.orgNameOverride
        : loaderData.job.org?.name;

      return (
        <RouteModule
          title={`${loaderData.job.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['JOB']}`}
          ogImage={
            loaderData.job.isPostedAnonymously
              ? null
              : imageUrlForOgMeta({
                  data:
                    loaderData.job.orgLogoOverride ?? loaderData.job.org?.logo,
                })
          }
          description={
            orgName
              ? i18n(
                  '%(org_name)s is now hiring for the position of %(job_name)s in %(city)s. Apply today.',
                  {
                    org_name: orgName,
                    job_name: loaderData.job.name,
                    city: loaderData.job.address.city,
                  },
                )
              : i18n(
                  'Employers are now hiring for the position of %(job_name)s in %(city)s. Apply today.',
                  {
                    job_name: loaderData.job.name,
                    city: loaderData.job.address.city,
                  },
                )
          }
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.job.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.job.url[loaderData.job.locale],
          }}
          schemaMarkups={[
            loaderData.job.schemaJobMarkup,
            loaderData.job.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
        >
          <CreateListingApplicationFromTmpStorageContainer
            listing={loaderData.job}
          >
            {({ initialListingApplicationId, listingApplicationFailure }) => (
              <ViewJobPage
                job={loaderData.job}
                publishedDetails={loaderData.publishedDetails}
                initialListingApplicationId={
                  initialListingApplicationId ?? loaderData.applicationId
                }
                initiallySaved={loaderData.saved}
                listingApplicationFailure={listingApplicationFailure}
              />
            )}
          </CreateListingApplicationFromTmpStorageContainer>
        </RouteModule>
      );
    }
    case 'EXPIRED_JOB':
      return (
        <RouteModule title={loaderData.job.name}>
          <LazyExpiredJobPage
            job={loaderData.job}
            publishedDetails={loaderData.publishedDetails}
            listingApplicationId={loaderData.applicationId}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_JOB':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <LazyUnpublishedJobPage unpublishedJob={loaderData.unpublishedJob} />
        </RouteModule>
      );
  }
}
